// POS → Restaurant → Ayuda (/pos/rest-ayuda). Patrón ayuda-de-modulo:
// checklist EN VIVO por local (RPC rest_ayuda_estado) + flujo del servicio +
// ¿cómo hago…? + tabla de atajos + glosario, con deep-links a cada pantalla.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ChevronRight, Keyboard, LifeBuoy, Link2, Loader2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { SelectorLocal, posRpc } from './shared';
import { useLocalRestaurant } from './restaurante';

interface AyudaData {
  checklist: Record<string, boolean | number>;
  kpis: {
    mesas: number; meseros: number; cuentas_abiertas: number;
    reservas_hoy: number; en_espera: number; platos_con_receta: number;
  };
}

function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}

const CHECKS: { key: string; label: string; ruta: string; titulo: string; ayuda: string; advisory?: boolean }[] = [
  { key: 'config', label: 'Configuración del local guardada', ruta: '/pos/rest-config', titulo: 'Configuración Restaurant', ayuda: '10% de servicio (si aplica) y umbrales del KDS; también siembra los productos internos del cobro.' },
  { key: 'areas', label: 'Áreas del salón creadas', ruta: '/pos/rest-mesas', titulo: 'Áreas y Mesas', ayuda: 'Salón, terraza, barra… son las pestañas del plano.' },
  { key: 'mesas', label: 'Mesas dibujadas en el plano', ruta: '/pos/rest-mesas', titulo: 'Áreas y Mesas', ayuda: 'Cada mesa con su NÚMERO (la vía de selección por teclado), forma y capacidad.' },
  { key: 'meseros', label: 'Meseros con PIN', ruta: '/pos/rest-meseros', titulo: 'Meseros', ayuda: 'El mesero se identifica con su PIN de 4-6 dígitos para abrir cuentas; no necesita usuario del sistema.' },
  { key: 'supervisor', label: 'Al menos un SUPERVISOR', ruta: '/pos/rest-meseros', titulo: 'Meseros', ayuda: 'Sin supervisor nadie puede anular un plato ya comandado (control anti-fraude).' },
  { key: 'estaciones', label: 'Estaciones de preparación', ruta: '/pos/rest-estaciones', titulo: 'Comanda y Estaciones', ayuda: 'Cocina, fría, bar… destino de las comandas y del Kitchen Display.' },
  { key: 'ruteo', label: 'Categorías ruteadas a estaciones', ruta: '/pos/rest-estaciones', titulo: 'Comanda y Estaciones', ayuda: 'Sin ruteo, todo va a la primera estación.', advisory: true },
  { key: 'caja_turno', label: 'Caja del local con turno abierto', ruta: '/pos/apertura', titulo: 'Apertura de caja', ayuda: 'El COBRO exige una caja con sesión abierta (el mesero comanda sin caja; el cajero cobra).', advisory: true },
  { key: 'recetas', label: 'Platos con receta (escandallo)', ruta: '/pos/rest-recetas', titulo: 'Recetas y Food Cost', ayuda: 'Opcional: descarga los ingredientes del kardex al cobrar y calcula el food cost.', advisory: true },
];

const FLUJO = [
  { n: 1, titulo: 'Configura el local', detalle: 'Áreas y mesas, meseros con PIN, estaciones y (si aplica) el 10% de servicio.', ruta: '/pos/rest-config', tituloTab: 'Configuración Restaurant' },
  { n: 2, titulo: 'Sienta al cliente', detalle: 'En el Salón: número de mesa + Enter y "Abrir cuenta" con el PIN del mesero. Con reservas o lista de espera, primero márcalas.', ruta: '/pos/rest-salon', tituloTab: 'Salón' },
  { n: 3, titulo: 'Toma el pedido', detalle: 'F1 busca el plato, elige curso (entrada/fuerte/postre), asiento y modificadores. El cliente también puede pedir desde el QR de la mesa.', ruta: '/pos/rest-salon', tituloTab: 'Salón' },
  { n: 4, titulo: 'Comanda a cocina (F4)', detalle: 'Envía SOLO lo nuevo: imprime la comanda por estación y aparece en el Kitchen Display. Los cursos siguientes quedan retenidos hasta el FIRE (F3).', ruta: '/pos/rest-kds', tituloTab: 'Kitchen Display' },
  { n: 5, titulo: 'Cocina y despacho', detalle: 'Cada estación avanza sus tickets (Espacio); el EXPEDIDOR despacha cuando todas terminaron, así los platos de la mesa salen juntos.', ruta: '/pos/rest-kds', tituloTab: 'Kitchen Display' },
  { n: 6, titulo: 'Pre-cuenta (F5)', detalle: 'Proforma "NO ES FACTURA" con el 10% desglosado. Los datos del cliente se piden recién al cobrar.', ruta: '/pos/rest-salon', tituloTab: 'Salón' },
  { n: 7, titulo: 'Cobra (F9)', detalle: 'Factura SRI o nota de venta, multipago, división por ítem/asiento o en N partes iguales, propina voluntaria aparte. La mesa queda "por limpiar".', ruta: '/pos/rest-salon', tituloTab: 'Salón' },
  { n: 8, titulo: 'Cierre del turno', detalle: 'Arqueo normal del POS + Reportes Restaurant: ventas y 10% por mesero (base del reparto) y tiempos de cocina.', ruta: '/pos/rest-reportes', tituloTab: 'Reportes Restaurant' },
];

const COMO: { q: string; pasos: string[]; enlaces: { to: string; t: string }[] }[] = [
  {
    q: '¿Cómo abro una mesa y tomo el pedido?',
    pasos: [
      'En el Salón teclea el NÚMERO de la mesa + Enter (o haz clic). Si está libre, ingresa comensales y el PIN del mesero.',
      'F1 abre el buscador: elige el plato; si tiene modificadores (término, extras…) se piden ahí mismo, con curso, asiento y nota a cocina.',
      'Las líneas quedan "nuevas" (ámbar) hasta que envíes la ronda con F4 — antes de comandar puedes quitarlas libremente con F6/Supr.',
    ],
    enlaces: [{ to: '/pos/rest-salon', t: 'Salón' }],
  },
  {
    q: '¿Cómo funcionan los CURSOS y el FIRE?',
    pasos: [
      'Cada línea lleva su curso: 1º entrada/bebida, 2º fuerte, 3º postre (F2 lo rota en el buscador).',
      'Al enviar (F4) solo el curso MÁS BAJO entra a cocina; los siguientes quedan RETENIDOS (naranja).',
      'Cuando la mesa esté lista para el siguiente plato: F3 (fire) desde la cuenta, o la tecla C en el Expedidor — imprime su comanda al momento.',
    ],
    enlaces: [{ to: '/pos/rest-salon', t: 'Salón' }, { to: '/pos/rest-kds', t: 'Kitchen Display' }],
  },
  {
    q: '¿Cómo anulo un plato YA comandado?',
    pasos: [
      'Selecciona la línea (↑/↓) y pulsa Supr: pedirá el PIN de un SUPERVISOR y el motivo (error, devuelto, cortesía).',
      'Queda auditado (quién, cuándo, por qué) e imprime un ticket «ANULADO» en la estación para que cocina pare el plato.',
      'El reporte por mesero muestra las anulaciones de cada uno — revísalo cada semana: los voids son el fraude #1 en restaurantes.',
    ],
    enlaces: [{ to: '/pos/rest-salon', t: 'Salón' }, { to: '/pos/rest-reportes', t: 'Reportes Restaurant' }],
  },
  {
    q: '¿Cómo divido la cuenta?',
    pasos: [
      'Por ÍTEM: en el cobro (F9) marca "Dividir por ítem" y elige las líneas — o usa los chips "Asiento N" si asignaste asientos.',
      'En PARTES IGUALES: marca "Dividir en partes iguales" e indica N; cada parte genera su documento y la última absorbe el remanente del redondeo.',
      'Una vez fijada la división en partes ya no se mezcla con el cobro por ítem, y la mesa se cierra al cobrar la última.',
    ],
    enlaces: [{ to: '/pos/rest-salon', t: 'Salón' }],
  },
  {
    q: '¿Cómo cobro y qué pasa con el 10% de servicio?',
    pasos: [
      'F5 imprime la PRE-CUENTA (no es factura); F9 abre el cobro: caja con turno abierto, cliente (RUC/cédula o Consumidor Final) y multipago.',
      'El 10% (si el local lo cobra) se factura como línea «Incluye 10% de servicios-propina-TIP» SIN IVA y sale por mesero en Reportes para su reparto mensual.',
      'La propina VOLUNTARIA va en su campo aparte: no entra a la factura, solo al arqueo de caja.',
      'La factura viaja al SRI por el mismo motor del POS (serie de la caja, clave de acceso, emisión offline si no hay red).',
      'CON TARJETA: si el local tiene habilitado el enlace de pago, el botón «Cobrar con tarjeta (QR en la mesa)» genera un QR que el comensal escanea desde su sitio, sin que la tarjeta salga de sus manos ni el mesero cargue el datáfono.',
      'Al autorizarse, la autorización, la marca, los últimos 4, el lote y el adquirente real quedan guardados solos: es lo que luego cuadra con la liquidación del banco.',
      'Con la tarjeta ya cobrada la ventana NO deja salir sin cobrar la cuenta — si se cerrara, el cobro quedaría suelto y el comensal habría pagado sin factura.',
      'Se habilita por separado del POS de mostrador en POS → Configuración → Pasarela de cobro, módulo «POS Restaurant».',
    ],
    enlaces: [{ to: '/pos/rest-config', t: 'Configuración Restaurant' }, { to: '/pos/pasarela', t: 'Pasarela de cobro' }, { to: '/pos/apertura', t: 'Apertura de caja' }],
  },
  {
    q: '¿Cómo transfiero una cuenta de mesa o uno dos mesas?',
    pasos: [
      'F6 transfiere la cuenta a otra mesa LIBRE (teclea el número destino).',
      'F7 une la cuenta seleccionada a la cuenta de otra mesa: las líneas se mueven y la cuenta origen se cancela con rastro.',
      'También puedes cambiar el MESERO de la cuenta (panel derecho) — clave para que el cierre por mesero cuadre.',
    ],
    enlaces: [{ to: '/pos/rest-salon', t: 'Salón' }],
  },
  {
    q: '¿Cómo se usa el Kitchen Display y el Expedidor?',
    pasos: [
      'Modo ESTACIÓN: cada pantalla filtra su estación; los tickets se pintan verde→amarillo→rojo según el tiempo (umbrales en Configuración). Espacio avanza (nuevo→preparando→listo), R recupera el último listo.',
      'Modo EXPEDIDOR: ve el ticket completo con el semáforo de cada estación y SOLO despacha cuando todas marcaron listo — así la mesa sale junta. C dispara los cursos en espera.',
      'Un bump bar físico es un teclado USB: todo el KDS se opera sin tocar la pantalla.',
    ],
    enlaces: [{ to: '/pos/rest-kds', t: 'Kitchen Display' }, { to: '/pos/rest-config', t: 'Configuración Restaurant' }],
  },
  {
    q: '¿Comanda impresa: terminal o impresora de red?',
    pasos: [
      'Modo TERMINAL (default): la comanda se imprime en la impresora del equipo que comanda, un ticket por estación.',
      'Modo RED: en Comanda y Estaciones asigna a la estación la IP:puerto de su impresora térmica (típico :9100); el print-agent (servicio en el servidor) le manda la comanda ESC/POS directo — el terminal ya no la imprime.',
      'El checklist de arriba avisa si hay estaciones en red y el print-agent no está conectado.',
    ],
    enlaces: [{ to: '/pos/rest-estaciones', t: 'Comanda y Estaciones' }],
  },
  {
    q: '¿Cómo activo el pedido por QR en la mesa?',
    pasos: [
      'En Áreas y Mesas selecciona la mesa y pulsa el botón QR: imprime el código y pégalo en la mesa (el enlace es permanente).',
      'El cliente escanea, ve la carta y envía su pedido: entra como líneas PENDIENTES con el badge 📱QR en la cuenta — el mesero las revisa y comanda con F4.',
      'Por defecto el mesero sienta primero (Salón → abrir cuenta con su PIN) y recién ahí el QR deja ordenar. Si en Configuración activas «Autoservicio», el propio cliente abre su mesa desde el QR (indica cuántos son) sin esperar al mesero.',
      'El precio siempre lo pone el sistema, nunca el teléfono del cliente.',
    ],
    enlaces: [{ to: '/pos/rest-mesas', t: 'Áreas y Mesas' }, { to: '/pos/rest-config', t: 'Configuración Restaurant' }],
  },
  {
    q: '¿Cómo manejo delivery y los agregadores (PedidosYa/UberEats)?',
    pasos: [
      'Delivery propio: "Para llevar" (F3) → tipo delivery con nombre/teléfono/dirección; asigna el REPARTIDOR en el panel Entrega y avanza pendiente→despachado→entregado.',
      'Agregadores: configura el proveedor en Pedidos Externos (API key cifrada, % de comisión). Los pedidos llegan a la bandeja; ACEPTAR (con PIN) crea la cuenta delivery lista para comandar.',
      'Mientras no haya credenciales de partner, el modo SIMULADO genera pedidos de prueba para entrenar el flujo completo.',
    ],
    enlaces: [{ to: '/pos/rest-salon', t: 'Salón' }, { to: '/pos/rest-pedidos-ext', t: 'Pedidos Externos' }, { to: '/pos/rest-meseros', t: 'Meseros' }],
  },
  {
    q: '¿Cómo armo las recetas y leo el food cost?',
    pasos: [
      'Los PLATOS son productos SIN control de stock (Inventario); las bebidas embotelladas sí controlan y descuentan como siempre.',
      'En Recetas y Food Cost busca el plato y arma su escandallo: ingredientes + cantidades. El costo usa el promedio del local.',
      'Al COBRAR, los ingredientes se descargan del kardex automáticamente (y se reponen si anulas la venta).',
      'Food cost sano: 28-35%. El semáforo marca en rojo los platos que pierden margen — sube el precio o ajusta la porción.',
    ],
    enlaces: [{ to: '/pos/rest-recetas', t: 'Recetas y Food Cost' }],
  },
  {
    q: '¿Reservas y lista de espera?',
    pasos: [
      'Reservas: agenda del día con mesa, hora y cliente; la mesa se pinta MORADA en el plano con la hora. Marca «Llegó» al sentarla (o no-show). El sistema rechaza dos reservas de la misma mesa a menos de 90 minutos.',
      'Lista de espera: el botón ⏳ del Salón lleva la cola (nombre, personas, minutos); «Sentar» pide el número de mesa libre.',
    ],
    enlaces: [{ to: '/pos/rest-reservas', t: 'Reservas' }, { to: '/pos/rest-salon', t: 'Salón' }],
  },
  {
    q: '¿Qué reportes tengo y para qué sirven?',
    pasos: [
      'Por MESERO: cuentas, comensales, ventas, 10% de servicio (la base del reparto mensual entre trabajadores) y líneas anuladas (control de voids).',
      'TIEMPOS DE COCINA: por estación, promedio/percentil 90/máximo de comanda→listo y cuántas líneas pasaron del umbral rojo — para dimensionar la cocina.',
      'Las ventas del Restaurant también alimentan los reportes normales del POS (ventas del día, dashboard, cierre Z).',
    ],
    enlaces: [{ to: '/pos/rest-reportes', t: 'Reportes Restaurant' }, { to: '/pos/dashboard', t: 'Dashboard POS' }],
  },
];

const ATAJOS: { ambito: string; teclas: [string, string][] }[] = [
  { ambito: 'Salón — plano', teclas: [
    ['Nº + Enter', 'Seleccionar/abrir la mesa por su número (la vía principal)'],
    ['Tab', 'Cambiar de área (Salón, Terraza…)'], ['Flechas', 'Navegar entre mesas'],
    ['F2 / Enter', 'Abrir cuenta o entrar a la cuenta'], ['F3', 'Cuenta para llevar / delivery / barra'],
    ['F5', 'Pre-cuenta de la mesa seleccionada'], ['F6', 'Transferir de mesa'], ['F7', 'Unir cuentas'],
    ['F8', 'Marcar sucia / limpia'], ['F9', 'Cobrar'], ['Esc', 'Limpiar selección'],
  ]},
  { ambito: 'Salón — cuenta', teclas: [
    ['F1', 'Buscar y agregar producto'], ['F3', 'FIRE del siguiente curso retenido'],
    ['F4', 'Enviar la ronda a cocina'], ['F5', 'Pre-cuenta'], ['F6 / Supr', 'Quitar línea nueva o ANULAR comandada (supervisor)'],
    ['F9', 'Cobrar'], ['↑/↓', 'Seleccionar línea'], ['Esc', 'Volver al plano'],
  ]},
  { ambito: 'Buscador (F1)', teclas: [
    ['↑/↓ + Enter', 'Elegir producto'], ['F2', 'Rotar el curso (entrada/fuerte/postre)'],
    ['F3', 'Rotar el asiento'],
  ]},
  { ambito: 'Kitchen Display', teclas: [
    ['←/→ o 1-9', 'Elegir ticket'], ['Espacio / Enter', 'Avanzar (nuevo→preparando→listo) · en Expedidor: DESPACHAR'],
    ['R', 'Recall del último listo (modo estación)'], ['C', 'FIRE del primer curso en espera (expedidor)'],
    ['F', 'Pantalla completa'],
  ]},
];

const GLOSARIO: { t: string; d: string }[] = [
  { t: 'Cuenta (orden)', d: 'La comanda viva de la mesa: acumula rondas durante todo el servicio; la factura nace recién al cobrar.' },
  { t: 'Ronda', d: 'Cada "enviar a cocina": comanda SOLO lo nuevo, nunca reimprime lo anterior.' },
  { t: 'Curso / fire', d: 'Tiempos del menú (entrada→fuerte→postre). Los cursos siguientes quedan retenidos y se disparan (fire) cuando la mesa está lista.' },
  { t: 'Comanda', d: 'Ticket de cocina por estación: cantidades, modificadores y notas — sin precios ni datos fiscales.' },
  { t: 'KDS / bump / expedidor', d: 'Pantalla de cocina; "bump" = marcar listo (Espacio). El expedidor consolida las estaciones y despacha la mesa completa.' },
  { t: '10% de servicio vs propina voluntaria', d: 'El 10% es un cargo legal que va EN la factura (sin IVA) y se reparte entre los trabajadores; la propina voluntaria es aparte y solo entra al arqueo.' },
  { t: 'Void (anulación)', d: 'Línea comandada anulada con PIN de supervisor + motivo. Auditada y reportada por mesero.' },
  { t: 'Asiento', d: 'A quién pertenece cada plato (asiento 1, 2…): permite servir sin preguntar y cobrar por persona.' },
  { t: 'Food cost', d: 'Costo de la receta ÷ precio del plato. Sano entre 28% y 35%; arriba, el plato pierde margen.' },
  { t: 'QR de mesa', d: 'Enlace permanente de la carta de esa mesa: el cliente pide desde su teléfono y el mesero confirma. El token del QR es la única llave.' },
  { t: 'Agregador', d: 'Plataforma de delivery (PedidosYa, UberEats…). Sus pedidos entran a la bandeja y se convierten en cuentas delivery; la comisión sirve para conciliar su liquidación.' },
];

function Acordeon({ q, children }: { q: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-md">
      <button className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-left" onClick={() => setOpen((o) => !o)}>
        {q} {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
      </button>
      {open && <div className="px-3 pb-3 text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}

export function AyudaRestaurant() {
  const ir = useIr();
  const { locationId, setLocationId, locations } = useLocalRestaurant();
  const { data, isFetching } = useQuery<AyudaData>({
    queryKey: ['rest-ayuda', locationId],
    queryFn: () => posRpc<AyudaData>('rest_ayuda_estado', { _location: locationId }),
    enabled: !!locationId,
    staleTime: 0, refetchOnMount: 'always',
  });

  const Ir = ({ to, t }: { to: string; t: string }) => (
    <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => ir(to, t)}>
      <Link2 className="w-3.5 h-3.5" /> {t}
    </button>
  );

  const estacionesRed = Number(data?.checklist?.estaciones_red ?? 0);
  const printAgentOk = Boolean(data?.checklist?.print_agent);

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <LifeBuoy className="w-5 h-5" /> Ayuda — Restaurant
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </h1>
        <SelectorLocal locationId={locationId} setLocationId={setLocationId} locations={locations} />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Listo para operar (este local)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {CHECKS.map((c) => {
            const ok = Boolean(data?.checklist?.[c.key]);
            return (
              <div key={c.key} className="flex items-start gap-2 text-sm">
                {ok
                  ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
                  : <XCircle className={`w-4 h-4 mt-0.5 shrink-0 ${c.advisory ? 'text-amber-500' : 'text-red-500'}`} />}
                <div>
                  <span className={ok ? '' : 'font-medium'}>{c.label}</span>
                  <span className="text-muted-foreground"> — {c.ayuda}</span>{' '}
                  {!ok && <Ir to={c.ruta} t={c.titulo} />}
                </div>
              </div>
            );
          })}
          {estacionesRed > 0 && (
            <div className="flex items-start gap-2 text-sm">
              {printAgentOk
                ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
                : <XCircle className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />}
              <div>
                <span className={printAgentOk ? '' : 'font-medium'}>Print-agent conectado</span>
                <span className="text-muted-foreground"> — hay {estacionesRed} estación(es) con impresora de RED: sin el servicio print-agent sus comandas no se imprimen (lo activa TI vía systemd).</span>
              </div>
            </div>
          )}
          {data && (
            <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
              {data.kpis.mesas} mesa(s) · {data.kpis.meseros} mesero(s) · {data.kpis.cuentas_abiertas} cuenta(s) abiertas ·
              {' '}{data.kpis.reservas_hoy} reserva(s) hoy · {data.kpis.en_espera} en espera ·
              {' '}{data.kpis.platos_con_receta} plato(s) con receta
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">El flujo del servicio</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {FLUJO.map((f) => (
            <div key={f.n} className="flex items-start gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{f.n}</div>
              <div>
                <span className="font-medium">{f.titulo}.</span>{' '}
                <span className="text-muted-foreground">{f.detalle}</span>{' '}
                <Ir to={f.ruta} t={f.tituloTab} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">¿Cómo hago…?</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {COMO.map((c) => (
            <Acordeon key={c.q} q={c.q}>
              <ol className="list-decimal ml-5 space-y-1">
                {c.pasos.map((p, i) => <li key={i}>{p}</li>)}
              </ol>
              <div className="flex flex-wrap gap-3 mt-2">
                {c.enlaces.map((e) => <Ir key={e.to} to={e.to} t={e.t} />)}
              </div>
            </Acordeon>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Keyboard className="w-4 h-4" /> Atajos de teclado</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          {ATAJOS.map((a) => (
            <div key={a.ambito}>
              <div className="text-sm font-medium mb-1">{a.ambito}</div>
              <table className="w-full text-xs">
                <tbody>
                  {a.teclas.map(([k, d]) => (
                    <tr key={k} className="border-b last:border-0">
                      <td className="py-1 pr-2 whitespace-nowrap"><kbd className="px-1.5 py-0.5 rounded border bg-muted font-mono">{k}</kbd></td>
                      <td className="py-1 text-muted-foreground">{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Glosario</CardTitle></CardHeader>
        <CardContent className="grid gap-2 md:grid-cols-2">
          {GLOSARIO.map((g) => (
            <div key={g.t} className="text-sm">
              <span className="font-medium">{g.t}:</span>{' '}
              <span className="text-muted-foreground">{g.d}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
